import { TGoogleRoutePoint } from "../../../api/types/Map";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { APP_CONFIG } from "../../../constants/constants";
import { evaluateTrip } from "../util/tripEvaluator";
import {
  getGoogleRoutePointsFromTripPoints,
  gSnapPointsToRoad,
  polylineConfigLineStyles,
  renderRouteWithPolylines,
} from "./helper";
import { RenderRoute } from "./renderRoute.class";
import { RenderRouteDirectionsService } from "./route-directionsservice";
import { RenderRouteSnapToRoad } from "./route-snaptoroad";
import {
  bridgePlausibility,
  sanitizeTripPointsForRouting,
  snappedPathPlausibility,
  straightLineRoutePoints,
} from "./routeQuality";
import { approxMetersBetween } from "../multiDay/geo";

/**
 * Hybrid Trip Segmentation & Intelligent Route Reconstruction Approach
 * --------------------------------------------------------------------
 * To improve route accuracy and ensure vehicle trips stay aligned with
 * actual roads, we propose a Hybrid Segmentation and Routing Engine rather
 * than relying on a single mapping method for the full trip.
 *
 * Vehicle GPS data can often be inconsistent due to:
 * - Dense points in some areas (frequent reporting)
 * - Sparse points in others (large gaps)
 * - GPS drift, noise, or unrealistic jumps
 *
 * Using only one method (straight polyline, Snap-to-Road, or Directions API)
 * across the full journey can produce inaccurate routes such as lines
 * cutting through buildings, parks, or non-drivable paths.
 *
 * 1) First and Most Crucial Step: We analyze and segment the trip into
 * logical chunks based on:
 * - Distance between consecutive points
 * - Time gaps
 * - Speed anomalies
 * - GPS consistency
 *
 * 2) Each chunk is then classified by data quality:
 * Dense segments → Snap-to-Road API for precise road alignment
 * Moderate & Sparse segments → Google Directions Service to reconstruct likely road path
 *
 * High-Level Flow
 * Raw GPS Data → Data Cleaning → Trip Segmentation → Segment Classification → Best-fit Routing Method → Route Stitching → Final Unified Route
 */

type TRoutePointsChunk = {
  points: TGoogleRoutePoint[];
  renderMethod: keyof typeof APP_CONFIG.MAPS.ASSET_MAP.ROUTE_DISPLAY.METHODS;
};

const CHUNK_BOUNDARY_EPSILON = 1e-6;

const areSameLocation = (
  pointA?: TGoogleRoutePoint,
  pointB?: TGoogleRoutePoint,
  epsilon = CHUNK_BOUNDARY_EPSILON
) => {
  if (!pointA || !pointB) return false;

  return (
    Math.abs(pointA.location.latitude - pointB.location.latitude) <= epsilon &&
    Math.abs(pointA.location.longitude - pointB.location.longitude) <= epsilon
  );
};

// total length in metres of a sequence of route points carrying plain
// lat/lng locations - used to measure a Directions bridge against the
// straight-line distance between its endpoints
const bridgePathLengthMeters = (points: TGoogleRoutePoint[]): number => {
  let total = 0;
  for (let index = 1; index < points.length; index++) {
    total += approxMetersBetween(
      {
        lat: points[index - 1].location.latitude,
        lng: points[index - 1].location.longitude,
      },
      {
        lat: points[index].location.latitude,
        lng: points[index].location.longitude,
      }
    );
  }
  return total;
};

const toVehicleMapDataPoint = (
  point: TGoogleRoutePoint
): VehicleMapDataPoint => {
  const tripPoint = point.tripPoint as VehicleMapDataPoint | undefined;

  return {
    ...(tripPoint || ({} as VehicleMapDataPoint)),
    latitude: point.location.latitude,
    longitude: point.location.longitude,
    timestamp: tripPoint?.timestamp ?? 0,
    creationtime: tripPoint?.creationtime ?? 0,
  } as VehicleMapDataPoint;
};

type ResolvedSegment = {
  points: TGoogleRoutePoint[];
  renderMethod: keyof typeof APP_CONFIG.MAPS.ASSET_MAP.ROUTE_DISPLAY.METHODS;
  // populated only when a preferred method was rejected, for debug logging
  fallbackReason?: string;
};

// Fetches one road-aligned candidate for a segment and normalises its shape
// to a plain-lat/lng TGoogleRoutePoint[]. Both underlying methods fail soft
// (resolve [] on error), so this never throws.
const fetchSegmentCandidate = async ({
  tripIndex,
  segmentPoints,
  method,
}: {
  tripIndex: number;
  segmentPoints: VehicleMapDataPoint[];
  method: "SNAP_TO_ROAD" | "DIRECTIONS_SERVICE";
}): Promise<TGoogleRoutePoint[]> => {
  if (method === "SNAP_TO_ROAD") {
    return RenderRouteSnapToRoad.getComputedRoutePoints({
      tripIndex,
      sanitizedTripPoints: segmentPoints,
      refString: "DEF",
    });
  }
  const directionsRoutePoints =
    await RenderRouteDirectionsService.getComputedRoutePoints({
      tripIndex,
      sanitizedTripPoints: segmentPoints,
      refString: "DEF",
    });
  return directionsRoutePoints.map((point) => ({
    ...point,
    location: {
      latitude: (point.location as any).latitude,
      longitude: (point.location as any).longitude,
    },
  }));
};

// Resolves a segment by trying each preferred method in order, accepting the
// first candidate that is non-empty and - for Snap-to-Road - passes the
// plausibility gate. Directions reconstructs a legal road path by
// construction, so it is accepted on non-emptiness alone; Snap-to-Road is the
// one that mis-snaps onto parallel streets, so it is the one gated. If no road
// method produces an acceptable result, falls back to the raw-GPS straight
// line so a route is never missing and never worse than the honest GPS trace.
const resolveSegmentWithFallback = async ({
  tripIndex,
  segmentPoints,
  preferredOrder,
}: {
  tripIndex: number;
  segmentPoints: VehicleMapDataPoint[];
  preferredOrder: ("SNAP_TO_ROAD" | "DIRECTIONS_SERVICE")[];
}): Promise<ResolvedSegment> => {
  const rejectionReasons: string[] = [];

  for (const method of preferredOrder) {
    const candidatePoints = await fetchSegmentCandidate({
      tripIndex,
      segmentPoints,
      method,
    });

    if (candidatePoints.length < 2) {
      rejectionReasons.push(`${method}: empty`);
      continue;
    }

    if (method === "SNAP_TO_ROAD") {
      const verdict = snappedPathPlausibility(candidatePoints, segmentPoints);
      if (!verdict.isPlausible) {
        rejectionReasons.push(`${method}: implausible (${verdict.reason})`);
        continue;
      }
    }

    return {
      points: candidatePoints,
      renderMethod: method,
      fallbackReason:
        rejectionReasons.length > 0
          ? `fell back past ${rejectionReasons.join(", ")}`
          : undefined,
    };
  }

  // honest floor - the raw device fixes as a straight-line polyline
  return {
    points: straightLineRoutePoints(segmentPoints),
    renderMethod: "POINT_TO_POINT",
    fallbackReason: `raw GPS floor after ${rejectionReasons.join(", ")}`,
  };
};

export class RenderRouteDefault extends RenderRoute {
  // static method to get the computed route points by snapping the original trip points to road using google snap to road API,
  // which can be used independently wherever we need to get the snapped points for a given set of trip points, without needing to create an instance of the class and calling
  static getComputedRoutePoints = async ({
    tripIndex,
    sanitizedTripPoints,
  }: {
    tripIndex: number;
    sanitizedTripPoints: VehicleMapDataPoint[];
  }): Promise<TGoogleRoutePoint[]> => {
    // snap the trip points to road using google snap to road API and get the interpolated points in the response to render the route using those points
    return new Promise((resolve, reject) => {
      gSnapPointsToRoad({
        tripIndex,
        tripPoints: sanitizedTripPoints,
        successCallback: ({ pointsOriginal, pointsInterpolated }) => {
          if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
            console.log(
              `========= TRIP#${tripIndex} SNAP TO ROAD SUCCESS [getComputedRoutePoints]`,
              { pointsOriginal, pointsInterpolated }
            );
          resolve(pointsInterpolated);
        },
        errorCallback: (points) => {
          if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
            console.log(
              `========= TRIP#${tripIndex} SNAP TO ROAD ERROR [getComputedRoutePoints]`,
              points
            );
          reject(points);
        },
      });
    });
  };

  preRender = async () => {
    const { tripIndex } = this;
    // Clean the GPS fixes before anything routes them. Invalid (0,0)/out-of-
    // range fixes and teleport glitches are removed here so they can't draw a
    // spike AND can't skew the segmenter's distance percentiles, which decide
    // snap-vs-directions for the whole trip. See sanitizeTripPointsForRouting.
    const sanitizedTripPoints = sanitizeTripPointsForRouting(
      this.sanitizedTripPoints
    );
    if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION &&
      sanitizedTripPoints.length !== this.sanitizedTripPoints.length)
      console.info(
        `[RNDRT:DEF] T#${tripIndex} SANITIZE dropped ${this.sanitizedTripPoints.length - sanitizedTripPoints.length} of ${this.sanitizedTripPoints.length} fixes`
      );
    // evaluate the trip
    const tripEvaluationResult = evaluateTrip(sanitizedTripPoints);
    if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION)
      console.log(
        `[RNDRT:DEF] T#${tripIndex} EVALU8 segs=${tripEvaluationResult.segments.length} pts=${sanitizedTripPoints.length}`,
        tripEvaluationResult
      );

    // for each segment, resolve its route points through an ordered fallback
    // floor rather than a single method with no guard on the output.
    //
    // The density decides the PREFERRED method, but every road-aligned result
    // is now quality-gated before it is accepted:
    //   - DENSE  prefers SNAP_TO_ROAD (then DIRECTIONS, then raw GPS)
    //   - others prefer DIRECTIONS    (then SNAP_TO_ROAD, then raw GPS)
    // A candidate is accepted only if it is non-empty AND - for Snap-to-Road -
    // passes the plausibility gate (snapped fixes didn't jump to a parallel
    // street, the path isn't grossly inflated). If no road method yields a
    // plausible result, the segment falls back to the raw-GPS straight line,
    // which is angular but only ever shows where the device actually was -
    // never a fabricated detour through a house or down the wrong street.
    const routePointsChunks: TRoutePointsChunk[] = [];
    for (const segment of tripEvaluationResult.segments) {
      const preferredOrder: ("SNAP_TO_ROAD" | "DIRECTIONS_SERVICE")[] =
        segment.densityStatus === "DENSE"
          ? ["SNAP_TO_ROAD", "DIRECTIONS_SERVICE"]
          : ["DIRECTIONS_SERVICE", "SNAP_TO_ROAD"];

      const resolved = await resolveSegmentWithFallback({
        tripIndex,
        segmentPoints: segment.points,
        preferredOrder,
      });

      if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION) {
        console.info(
          `[RNDRT:DEF] T#${tripIndex} EVALU8 SEG ${segment.densityStatus} in=${segment.points.length} -> ${resolved.renderMethod} out=${resolved.points.length}${resolved.fallbackReason ? ` (${resolved.fallbackReason})` : ""}`
        );
        console.log(
          `[RNDRT:DEF] T#${tripIndex} EVALU8 SEG src`,
          segment.points
        );
        console.log(
          `[RNDRT:DEF] T#${tripIndex} EVALU8 SEG result`,
          resolved.points
        );
      }
      routePointsChunks.push({
        points: resolved.points,
        renderMethod: resolved.renderMethod,
      });
    }
    if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION) {
      console.log(
        `[RNDRT:DEF] T#${tripIndex} EVALU8 CHUNKS total=${routePointsChunks.length}`,
        routePointsChunks
      );
    }

    const stitchedRoutePointsChunks: TRoutePointsChunk[] = [];
    for (let index = 0; index < routePointsChunks.length; index++) {
      const currentChunk = routePointsChunks[index];
      const nextChunk = routePointsChunks[index + 1];

      stitchedRoutePointsChunks.push(currentChunk);

      if (!nextChunk) continue;

      const currentChunkLastPoint =
        currentChunk.points[currentChunk.points.length - 1];
      const nextChunkFirstPoint = nextChunk.points[0];

      if (
        !currentChunkLastPoint ||
        !nextChunkFirstPoint ||
        areSameLocation(currentChunkLastPoint, nextChunkFirstPoint)
      ) {
        continue;
      }

      const bridgeRoutePoints =
        await RenderRouteDirectionsService.getComputedRoutePoints({
          tripIndex,
          sanitizedTripPoints: [
            toVehicleMapDataPoint(currentChunkLastPoint),
            toVehicleMapDataPoint(nextChunkFirstPoint),
          ],
          refString: "DEF(BRIDGE)", // add refString to identify the caller in debug logs
        }).catch(() => []);

      const normalizedBridgePoints: TGoogleRoutePoint[] = bridgeRoutePoints.map(
        (point) => ({
          ...point,
          location: {
            latitude: (point.location as any).latitude,
            longitude: (point.location as any).longitude,
          },
        })
      );

      // Gate the bridge. A Directions connector between two far-apart (or
      // barrier-separated) boundary points can come back as a multi-block
      // detour the vehicle never drove - the phantom-diagonal artefact. If
      // the road bridge is implausibly long versus the straight line between
      // the two boundary points, draw the honest straight connector instead.
      const bridgeRoadMeters =
        normalizedBridgePoints.length >= 2
          ? bridgePathLengthMeters(normalizedBridgePoints)
          : Infinity;
      const bridgeVerdict = bridgePlausibility(
        bridgeRoadMeters,
        currentChunkLastPoint.location,
        nextChunkFirstPoint.location
      );

      if (normalizedBridgePoints.length >= 2 && bridgeVerdict.isPlausible) {
        stitchedRoutePointsChunks.push({
          renderMethod: "DIRECTIONS_SERVICE",
          points: normalizedBridgePoints,
        });
        if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION) {
          console.info(
            `[RNDRT:DEF] T#${tripIndex} EVALU8 BRIDGE c${index}→c${index + 1} pts=${normalizedBridgePoints.length}`
          );
        }
      } else {
        // honest straight connector between the two chunk boundaries
        stitchedRoutePointsChunks.push({
          renderMethod: "POINT_TO_POINT",
          points: [currentChunkLastPoint, nextChunkFirstPoint],
        });
        if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION) {
          console.info(
            `[RNDRT:DEF] T#${tripIndex} EVALU8 BRIDGE c${index}→c${index + 1} STRAIGHT (${normalizedBridgePoints.length < 2 ? "empty bridge" : bridgeVerdict.reason})`
          );
        }
      }
    }

    // Remove only consecutive duplicates introduced by overlapping segment boundaries.
    // Update 5/1/2026 - Since we are now keeping chunks separate based on the method used to get the route points, we can have duplicate points at the chunk boundaries,
    // which is expected and desired for accurate rendering of the route, so we will not be removing duplicates across chunks.
    // const dedupedRoutePoints = routePoints.filter((point, index) => {
    //   if (index === 0) return true;
    //   const prevPoint = routePoints[index - 1];
    //   return !(
    //     prevPoint?.location?.latitude === point?.location?.latitude &&
    //     prevPoint?.location?.longitude === point?.location?.longitude
    //   );
    // });
    // return dedupedRoutePoints;

    return stitchedRoutePointsChunks;
  };

  render = async () => {
    const interpolatedTripPoints =
      (await this.preRender()) as TRoutePointsChunk[];
    // if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION) { console.log("XXinterpolatedTripPoints trip#", this.tripIndex, interpolatedTripPoints, "original sanitizedTripPoints", this.sanitizedTripPoints, "polylineConfig", this.polylineConfig); }

    const { props, tripIndex, sanitizedTripPoints, polylineConfig } = this;
    const routeVisibility = props.mapRef.current.routeViewsEnabled.DEFAULT;
    if (APP_CONFIG.DEBUG.MAPS_TRIPEVALUATION)
      console.log(
        `[RNDRT:DEF] T#${tripIndex} EVALU8 FINAL chunks=${interpolatedTripPoints.length}`
      );

    const gRoutePoints: TGoogleRoutePoint[] =
      getGoogleRoutePointsFromTripPoints({ tripPoints: sanitizedTripPoints });

    // render the route
    if (
      props?.selectedTripIndex !== null &&
      tripIndex !== props?.selectedTripIndex
    ) {
      // do nothing
    } else {
      // initially we were merging all the route points from different segments and then rendering the route using those merged points,
      // but now since we are keeping the chunks separate based on the method used to get the route points, we will render each chunk separately using the respective method
      // const mapElements = renderRouteWithPolylines({
      //   props,
      //   tripIndex,
      //   polylineConfig,
      //   gRoutePoints:
      //     !!interpolatedTripPoints && interpolatedTripPoints.length > 0
      //       ? interpolatedTripPoints.map((point) => ({
      //           ...point,
      //           location: new google.maps.LatLng(
      //             point.location.latitude,
      //             point.location.longitude
      //           ) as any, // strangely typescript is giving error if I directly typecast the location as google.maps.LatLng, so added 'as any'
      //         }))
      //       : gRoutePoints,
      //   routeVisibility,
      // });
      // this.setMapElements(mapElements);
      const allMapElements: any[] = [];
      for (const chunk of interpolatedTripPoints) {
        const mapElements = renderRouteWithPolylines({
          props,
          tripIndex,
          polylineConfig: {
            ...(chunk.renderMethod === "DIRECTIONS_SERVICE"
              ? polylineConfigLineStyles.DOTTED
              : polylineConfigLineStyles.SOLID_THICK),
            strokeColor: polylineConfig.strokeColor,
          },
          gRoutePoints: chunk.points.map((point) => ({
            ...point,
            location: new google.maps.LatLng(
              (point.location as any).latitude,
              (point.location as any).longitude
            ) as any, // strangely typescript is giving error if I directly typecast the location as google.maps.LatLng, so added 'as any'
          })),
          routeVisibility,
        });
        allMapElements.push(...mapElements);
      }
      this.setMapElements(allMapElements);
    }

    // set route visibility in mapRef
    // 1 for visible, 0 for hidden. Initially setting the visibility based on routeViewsEnabled,
    // and then it can be toggled using show/hide methods of the class which will also update this visibility value accordingly
    props.mapRef.current.objects.mTripsVisibility.push(routeVisibility ? 1 : 0);

    this.isInitialized = true; // set initialized flag to true after first render
  };
}
